import { userHotelDetails } from '../../../helpers/handleBrandAndHotelData';
import axiosInstance from '../../../helpers/interceptor';
import { featureFlagsHelpers } from '../featureFlags/featureFlagsHelpers';

// Request deduplication: track in-flight requests by cache key
const inFlightRequests = new Map<string, Promise<any>>();

// Cache with TTL: store results with expiration time
interface CacheEntry {
  data: any;
  expiresAt: number;
}
const cache = new Map<string, CacheEntry>();

// Cache TTL: 5 seconds to prevent rapid successive calls
const CACHE_TTL_MS = 5000;

// Generate cache key from hotelId and selectedClientId
const getCacheKey = (hotelId: string | number | null, selectedClientId: string | number | null): string => {
  return `ff_${hotelId || 'no-hotel'}_${selectedClientId || 'no-client'}`;
};

export const getFeatureFlags = async (forceRefresh: boolean = false) => {
  const { selectedHotel, selectedClient } = userHotelDetails();
  const cacheKey = getCacheKey(selectedHotel, selectedClient);

  // Check cache first (unless force refresh)
  if (!forceRefresh) {
    const cached = cache.get(cacheKey);
    if (cached && cached.expiresAt > Date.now()) {
      return cached.data;
    }
  }

  // Check if there's already an in-flight request for this cache key
  const inFlightRequest = inFlightRequests.get(cacheKey);
  if (inFlightRequest) {
    return inFlightRequest;
  }

  // Create new request
  const url = selectedHotel
    ? `${Env.API_URL}/features/check/?hotelId=${selectedHotel}`
    : `${Env.API_URL}/features/check`;

  const requestPromise = axiosInstance.get(url).then((response) => {
    const { data } = response;

    if (data.flags) {
      featureFlagsHelpers.setFeatureFlags(data);
    }
    if (data.userContext) {
      featureFlagsHelpers.setUserContext(data.userContext);
    }

    // Cache the result
    cache.set(cacheKey, {
      data,
      expiresAt: Date.now() + CACHE_TTL_MS,
    });

    // Remove from in-flight requests
    inFlightRequests.delete(cacheKey);

    return data;
  }).catch((error) => {
    // Remove from in-flight requests on error
    inFlightRequests.delete(cacheKey);
    throw error;
  });

  // Store the in-flight request
  inFlightRequests.set(cacheKey, requestPromise);

  return requestPromise;
};

export const clearFeatureFlagsAndUserContext = () => {
  featureFlagsHelpers.clearFeatureFlagsAndUserContext();
  // Clear cache and in-flight requests when feature flags are cleared
  cache.clear();
  inFlightRequests.clear();
};

// Debounced version for interceptor to prevent rapid successive calls
interface DebounceState {
  timer: NodeJS.Timeout | null;
  promise: Promise<any> | null;
  resolve: ((value: any) => void) | null;
  reject: ((error: any) => void) | null;
}

const DEBOUNCE_MS = 100; // 100ms debounce

const debounceState: DebounceState = {
  timer: null,
  promise: null,
  resolve: null,
  reject: null,
};

const resetDebounceState = (): void => {
  debounceState.timer = null;
  debounceState.promise = null;
  debounceState.resolve = null;
  debounceState.reject = null;
};

const executeDebounce = (forceRefresh: boolean): void => {
  getFeatureFlags(forceRefresh)
    .then((result) => {
      if (debounceState.resolve) {
        debounceState.resolve(result);
      }
    })
    .catch((error) => {
      if (debounceState.reject) {
        debounceState.reject(error);
      }
    })
    .finally(() => {
      resetDebounceState();
    });
};

export const getFeatureFlagsDebounced = (forceRefresh: boolean = false): Promise<any> => {
  // If there's already a pending debounced call, reset timer and return existing promise
  if (debounceState.promise && debounceState.timer) {
    clearTimeout(debounceState.timer);
    debounceState.timer = setTimeout(() => executeDebounce(forceRefresh), DEBOUNCE_MS);
    return debounceState.promise;
  }

  // Create new promise
  debounceState.promise = new Promise((resolve, reject) => {
    debounceState.resolve = resolve;
    debounceState.reject = reject;
  });

  // Set timer
  debounceState.timer = setTimeout(() => executeDebounce(forceRefresh), DEBOUNCE_MS);
  return debounceState.promise;
};
